export const NUMERIC_POI_MARKER_STYLES = ["circle", "pin"];

export const NUMERIC_PIN_MARKER_DEFAULT_PX = 50;
export const POI_HOVER_SIZE_TRANSITION_MS = 400;
export const POI_HOVER_SIZE_TRANSITION_EASING = "ease-in-out";

const POI_ICON_MIN = 16;
const POI_ICON_MAX = 96;
const POI_ICON_SIZE_DEFAULT_MARKER = 28;

export const MDI_MAP_MARKER_PATH =
  "M12 11.5A2.5 2.5 0 0 1 9.5 9A2.5 2.5 0 0 1 12 6.5A2.5 2.5 0 0 1 14.5 9a2.5 2.5 0 0 1-2.5 2.5M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7";

const PIN_NUMBER_COLOR = "#111111";

function clampPoiIconSizePx(n) {
  if (!Number.isFinite(n)) return POI_ICON_SIZE_DEFAULT_MARKER;
  return Math.min(POI_ICON_MAX, Math.max(POI_ICON_MIN, Math.round(n)));
}

export function normalizeNumericPoiMarkerStyle(value) {
  return value === "pin" ? "pin" : "circle";
}

export function getNumericPinIconSizePx(poi) {
  const raw = poi && poi.iconSize;
  if (raw != null && Number.isFinite(Number(raw))) {
    return clampPoiIconSizePx(Number(raw));
  }
  return NUMERIC_PIN_MARKER_DEFAULT_PX;
}

export function getNumericMarkerIconSizePx(poi) {
  if (normalizeNumericPoiMarkerStyle(poi?.icon) === "pin") {
    return getNumericPinIconSizePx(poi);
  }
  const raw = poi && poi.iconSize;
  if (raw != null && Number.isFinite(Number(raw))) {
    return clampPoiIconSizePx(Number(raw));
  }
  return POI_ICON_SIZE_DEFAULT_MARKER;
}

export function getNumericHoverSizePx(poi) {
  const raw = poi && poi.hoverSize;
  if (raw == null || !Number.isFinite(Number(raw))) return null;
  return clampPoiIconSizePx(Number(raw));
}

export function numericMarkerHoverScale(poi) {
  const hoverPx = getNumericHoverSizePx(poi);
  if (hoverPx == null) return null;
  const basePx = getNumericMarkerIconSizePx(poi);
  if (basePx <= 0) return null;
  return hoverPx / basePx;
}

/** Gap between cursor and bottom of floating tooltip (px). Matches MapScripting default. */
export const NUMERIC_POI_TOOLTIP_GAP_PX = 16;

export function getNumericPoiEffectiveVisualSizePx(poi, hovered = true) {
  const baseSize = getNumericMarkerIconSizePx(poi);
  if (!hovered) return baseSize;
  return getNumericHoverSizePx(poi) ?? baseSize;
}

function numericPoiTooltipSizeFactor(poi) {
  return normalizeNumericPoiMarkerStyle(poi?.icon) === "pin" ? 0.55 : 0.5;
}

/** Vertical offset from cursor to floating-label anchor for a given visual marker size. */
export function getNumericPoiTooltipOffsetYForVisualSize(poi, visualSizePx) {
  // NUMERIC_POI_TOOLTIP_GAP_PX
  return Math.round(visualSizePx * numericPoiTooltipSizeFactor(poi));
}

/** Vertical offset from cursor to floating-label anchor when hovering a numeric POI. */
export function getNumericPoiTooltipOffsetY(poi, { hovered = true } = {}) {
  return getNumericPoiTooltipOffsetYForVisualSize(
    poi,
    getNumericPoiEffectiveVisualSizePx(poi, hovered),
  );
}

/** Current uniform scale of `.cove-poi-marker-scalable` (1 when not scaled). */
export function readScalableMarkerScale(scalableEl) {
  if (!scalableEl || typeof getComputedStyle !== "function") return 1;
  const transform = getComputedStyle(scalableEl).transform;
  if (!transform || transform === "none") return 1;
  if (typeof DOMMatrixReadOnly !== "undefined") {
    try {
      return Math.abs(new DOMMatrixReadOnly(transform).a) || 1;
    } catch (_) {}
  }
  const match = transform.match(/^matrix\(([^)]+)\)$/);
  if (!match) return 1;
  const scale = Number.parseFloat(match[1].split(",")[0]);
  return Number.isFinite(scale) ? Math.abs(scale) : 1;
}

/** Tooltip offset from live marker DOM (tracks CSS scale transition during hover). */
export function getNumericPoiTooltipOffsetYFromMarker(poi, markerOrElement) {
  const markerRoot = resolveNumericMarkerRoot(markerOrElement);
  const baseSize = getNumericMarkerIconSizePx(poi);
  const scalable = markerRoot?.querySelector?.(".cove-poi-marker-scalable");
  const scale = readScalableMarkerScale(scalable);
  return getNumericPoiTooltipOffsetYForVisualSize(poi, baseSize * scale);
}

function resolveNumericMarkerRoot(markerOrElement) {
  if (!markerOrElement) return null;
  if (markerOrElement.nodeType === 1) return markerOrElement;
  return markerOrElement.getElement?.() ?? markerOrElement._icon ?? null;
}

/** Run `onFrame` each animation frame until the marker scale transition finishes. */
export function runDuringNumericMarkerScaleTransition(markerOrElement, onFrame) {
  const markerRoot = resolveNumericMarkerRoot(markerOrElement);
  const scalable = markerRoot?.querySelector?.(".cove-poi-marker-scalable");
  if (!scalable) {
    onFrame();
    return () => {};
  }

  let frame = null;
  let stopped = false;

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (frame != null) cancelAnimationFrame(frame);
    scalable.removeEventListener("transitionend", onTransitionEnd);
  };

  const onTransitionEnd = (e) => {
    if (e.propertyName !== "transform") return;
    onFrame();
    stop();
  };

  const loop = () => {
    if (stopped) return;
    onFrame();
    frame = requestAnimationFrame(loop);
  };

  onFrame();
  scalable.addEventListener("transitionend", onTransitionEnd);
  frame = requestAnimationFrame(loop);
  setTimeout(stop, POI_HOVER_SIZE_TRANSITION_MS + 80);
  return stop;
}

export function buildNumericCircleMarkerHtml({ bgColor, textColor, strokeColor, number, size }) {
  const numStr = String(number)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  const multi = numStr.length > 1;
  const vbW = multi ? 40 : 28;
  const vbH = 28;
  const cx = vbW / 2;
  const rx = multi ? 12 : 13;
  const fontSize = multi ? 11 : 12;
  return `<svg class="cove-numeric-circle-marker" xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${vbW} ${vbH}" aria-hidden="true" style="display:block;color:${bgColor}">
  <rect x="1" y="1" width="${vbW - 2}" height="${vbH - 2}" rx="${rx}" ry="${rx}" fill="currentColor" stroke="${strokeColor}" stroke-width="2"/>
  <text x="${cx}" y="14.5" text-anchor="middle" dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" font-size="${fontSize}" font-weight="600" fill="${textColor}">${numStr}</text>
</svg>`;
}

function pinBadgeMetrics(numStr) {
  const multi = numStr.length > 1;
  return {
    cx: 12,
    cy: 8.85,
    rx: multi ? 7.1 : 6.15,
    ry: multi ? 5.75 : 6.15,
    fontSize: multi ? 5.9 : 7.1,
  };
}

export function buildNumericPinMarkerHtml({ pinColor, number, size, numberColor = PIN_NUMBER_COLOR }) {
  const numStr = String(number);
  const { cx, cy, rx, ry, fontSize } = pinBadgeMetrics(numStr);

  return `<svg class="cove-numeric-pin-marker" xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true" style="display:block;color:${pinColor};touch-action:manipulation;-webkit-user-select:none;user-select:none;">
  <path fill="currentColor" d="${MDI_MAP_MARKER_PATH}"/>
  <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="#ffffff"/>
  <text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-family="Arial, Helvetica, sans-serif" font-size="${fontSize}" font-weight="600" fill="${numberColor}">${numStr}</text>
</svg>`;
}

/** Marker inner HTML (circle or pin) matching map numeric POI style. */
export function buildNumericMarkerHtml(poi, { bgColor, textColor, strokeColor, number, size }) {
  const markerStyle = normalizeNumericPoiMarkerStyle(poi?.icon);
  const markerSize = size ?? getNumericMarkerIconSizePx(poi);
  return markerStyle === "pin"
    ? buildNumericPinMarkerHtml({ pinColor: bgColor, number, size: markerSize })
    : buildNumericCircleMarkerHtml({ bgColor, textColor, strokeColor, number, size: markerSize });
}

export function wrapScalableMarkerHtml(innerHtml, { baseSize, transformOrigin }) {
  return `<div class="cove-poi-marker-scalable" style="width:${baseSize}px;height:${baseSize}px;transform-origin:${transformOrigin};transition:transform ${POI_HOVER_SIZE_TRANSITION_MS}ms ${POI_HOVER_SIZE_TRANSITION_EASING};will-change:transform;">${innerHtml}</div>`;
}

export function createNumericMapboxMarkerElement(poi, { bgColor, textColor, strokeColor, number }) {
  const style = normalizeNumericPoiMarkerStyle(poi.icon);
  const size = getNumericMarkerIconSizePx(poi);
  const inner =
    style === "pin"
      ? buildNumericPinMarkerHtml({ pinColor: bgColor, number, size })
      : buildNumericCircleMarkerHtml({ bgColor, textColor, strokeColor, number, size });
  const transformOrigin = style === "pin" ? `${size / 2}px ${size}px` : "center center";
  const element = document.createElement("div");
  element.className = "cove-numeric-poi-marker";
  element.style.cursor = "pointer";
  element.style.touchAction = "manipulation";
  element.innerHTML = wrapScalableMarkerHtml(inner, { baseSize: size, transformOrigin });
  return { element, anchor: style === "pin" ? "bottom" : "center" };
}

export function applyLeafletMarkerHoverScale(marker, poi, hovered) {
  const scale = numericMarkerHoverScale(poi);
  if (scale == null) return;
  const el = marker.getElement?.()?.querySelector(".cove-poi-marker-scalable");
  if (!el) return;
  el.style.transform = hovered ? `scale(${scale})` : "";
}

export function applyLeafletNumericMarkerVisualHover(marker, colors, hovered) {
  const el = marker.getElement?.();
  if (!el) return;
  const root = el.querySelector(".cove-poi-marker-scalable") ?? el;
  const target = hovered ? colors.hoverBg : colors.baseBg;
  const transition = `${POI_HOVER_SIZE_TRANSITION_MS}ms ${POI_HOVER_SIZE_TRANSITION_EASING}`;

  const pinSvg = root.querySelector("svg.cove-numeric-pin-marker, svg:not(.cove-numeric-circle-marker)");
  if (pinSvg) {
    pinSvg.style.transition = `color ${transition}`;
    pinSvg.style.color = target;
    return;
  }
  const circleSvg = root.querySelector("svg.cove-numeric-circle-marker");
  if (circleSvg) {
    circleSvg.style.transition = `color ${transition}`;
    circleSvg.style.color = target;
    return;
  }
  const badge = root.querySelector("div[style*='border-radius:9999px']");
  if (badge) {
    badge.style.transition = `background-color ${transition}`;
    badge.style.background = target;
  }
}

export function applyNumericDomMarkerHover(markerRoot, poi, colors, hovered) {
  const marker = { getElement: () => markerRoot };
  applyLeafletMarkerHoverScale(marker, poi, hovered);
  applyLeafletNumericMarkerVisualHover(marker, colors, hovered);
}
